import { useState, useEffect, useCallback } from "react";

interface Peer {
  id: string;
  ip: string;
  port: number;
  name: string | null;
  lastSeen: string | null;
  lastSync: string | null;
  createdAt: string;
}

function timeAgo(iso: string | null): string {
  if (!iso) return "never";
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function isOnline(lastSeen: string | null): boolean {
  if (!lastSeen) return false;
  return Date.now() - new Date(lastSeen).getTime() < 90_000; // seen within 90s
}

export default function PeersPage() {
  const [peers, setPeers] = useState<Peer[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [syncMsg, setSyncMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [newIp, setNewIp] = useState("");
  const [newPort, setNewPort] = useState("3000");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");

  const fetchPeers = useCallback(async () => {
    const r = await fetch("/api/sync/peers");
    if (r.ok) setPeers(await r.json());
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPeers();
    const interval = setInterval(fetchPeers, 15_000);
    return () => clearInterval(interval);
  }, [fetchPeers]);

  async function handleSyncNow() {
    setSyncing(true);
    setSyncMsg(null);
    const r = await fetch("/api/sync/now", { method: "POST" });
    const j = await r.json();
    setSyncing(false);
    setSyncMsg({ ok: r.ok, text: j.message ?? (r.ok ? "Sync started" : "Sync failed") });
    setTimeout(() => setSyncMsg(null), 4000);
    setTimeout(fetchPeers, 3000);
  }

  async function handleDiscover() {
    setDiscovering(true);
    await fetch("/api/sync/discover", { method: "POST" });
    setDiscovering(false);
    setTimeout(fetchPeers, 2000);
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    setAdding(true);
    setAddError("");
    const r = await fetch("/api/sync/peers", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ip: newIp, port: Number(newPort) }),
    });
    setAdding(false);
    if (!r.ok) {
      const j = await r.json();
      setAddError(j.error ?? "Failed to add peer");
      return;
    }
    setNewIp("");
    setNewPort("3000");
    setShowAdd(false);
    fetchPeers();
  }

  async function handleDelete(id: string, ip: string) {
    if (!confirm(`Remove peer ${ip}?`)) return;
    await fetch(`/api/sync/peers/${id}`, { method: "DELETE" });
    fetchPeers();
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Peers</h1>
          <p className="text-sm text-slate-500 mt-0.5">Devices on the Tailscale network running this app</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleDiscover} disabled={discovering}
            className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50">
            {discovering ? "Scanning…" : "Scan Network"}
          </button>
          <button onClick={handleSyncNow} disabled={syncing || peers.length === 0}
            className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
            {syncing ? "Syncing…" : "Sync Now"}
          </button>
          <button onClick={() => setShowAdd((v) => !v)}
            className="px-3 py-2 text-sm font-medium text-white bg-slate-700 rounded-md hover:bg-slate-800">
            {showAdd ? "Cancel" : "+ Add Peer"}
          </button>
        </div>
      </div>

      {syncMsg && (
        <p className={`text-sm font-medium ${syncMsg.ok ? "text-green-600" : "text-red-600"}`}>
          {syncMsg.text}
        </p>
      )}

      {showAdd && (
        <form onSubmit={handleAdd} className="bg-white rounded-lg border border-slate-200 p-5 space-y-4">
          <h2 className="text-sm font-semibold text-slate-700">Add Peer Manually</h2>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-slate-600 mb-1">Tailscale IP</label>
              <input type="text" required value={newIp} onChange={(e) => setNewIp(e.target.value)}
                placeholder="100.x.x.x"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="w-28">
              <label className="block text-xs font-medium text-slate-600 mb-1">Port</label>
              <input type="number" value={newPort} onChange={(e) => setNewPort(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          {addError && <p className="text-sm text-red-600">{addError}</p>}
          <button type="submit" disabled={adding}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50">
            {adding ? "Adding…" : "Add Peer"}
          </button>
        </form>
      )}

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <p className="p-6 text-sm text-slate-400">Loading…</p>
        ) : peers.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-sm text-slate-500 mb-1">No peers found yet.</p>
            <p className="text-xs text-slate-400">Click "Scan Network" to discover devices on Tailscale, or add one manually.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3">Device</th>
                <th className="text-left px-4 py-3">Address</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Last Sync</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {peers.map((peer) => {
                const online = isOnline(peer.lastSeen);
                return (
                  <tr key={peer.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {peer.name ?? <span className="text-slate-400 font-normal italic">unknown</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">
                      {peer.ip}:{peer.port}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${
                        online ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${online ? "bg-green-500" : "bg-slate-400"}`} />
                        {online ? "Online" : `Seen ${timeAgo(peer.lastSeen)}`}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">{timeAgo(peer.lastSync)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => handleDelete(peer.id, peer.ip)}
                        className="text-xs text-red-500 hover:underline">
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-slate-500 space-y-1">
        <p className="font-medium text-slate-600">How sync works</p>
        <p>Each device runs its own local database. Every 30 seconds, this device pulls new records from each peer and pushes its own changes. Conflicts are resolved by keeping the most recently updated version.</p>
        <p>Peers are discovered automatically from the Tailscale network, or you can add them manually by IP.</p>
      </div>
    </div>
  );
}
